import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createSupabaseClient } from "../../../hooks/useSupabase";
import { useAppContext } from "../../Unknown/AppContext";
import { useUIContext } from "../../Unknown/UIContext";
import useTranslations from "./useTranslations";
import getAppointmentAIReports, {
  DashboardAppointmentAIReport,
} from "./getAppointmentAIReports";
import useSystemConfig from "../../../hooks/useSystemConfig";
import supabaseClient from "../../../common/supabaseClient";

export const useTableAppointmentAIReports = (params: {
  centerIds: string[];
  isApprovedShown: boolean;
}) => {
  const { centerIds, isApprovedShown } = params;
  const { centers } = useAppContext();
  const supabase = useMemo(() => createSupabaseClient(), []);
  const translations = useTranslations();
  const {
    dataState: { data: config, status: configStatus },
  } = useSystemConfig();
  const { setAlert } = useUIContext();

  const timezone = centers.length === 1 ? centers[0].timezone : null;

  const [isLoading, setIsLoading] = useState(false);
  const [appointmentAIReports, setAppointmentAIReports] = useState<
    DashboardAppointmentAIReport[]
  >([]);

  const hasDataRef = useRef(false);

  const fetch = useCallback(async () => {
    try {
      if (!supabase) return;

      if (!hasDataRef.current) {
        setIsLoading(true);
      }

      const nonApprovedReportsData = await getAppointmentAIReports({
        supabase,
        centerIds,
        isApprovedShown: false,
        showApprovedReportsDays: config?.show_read_studies_duration_days || 0,
        timezone,
      });

      const approvedReportsData = isApprovedShown
        ? await getAppointmentAIReports({
            supabase,
            centerIds,
            isApprovedShown: true,
            showApprovedReportsDays:
              config?.show_read_studies_duration_days || 0,
            timezone,
          })
        : [];

      const combined = [...nonApprovedReportsData, ...approvedReportsData];
      hasDataRef.current = combined.length > 0;
      setAppointmentAIReports(combined);
    } catch (error) {
      setAlert({
        isShown: true,
        message: translations.defaultError,
        severity: "error",
      });

      setAppointmentAIReports([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    supabase,
    centerIds,
    config?.show_read_studies_duration_days,
    timezone,
    isApprovedShown,
    setAlert,
    translations.defaultError,
  ]);

  const fetchRef = useRef(fetch);
  useEffect(() => {
    fetchRef.current = fetch;
  }, [fetch]);

  useEffect(() => {
    if (configStatus === "success" && config?.show_read_studies_duration_days) {
      fetchRef.current();
    }

    const appointmentAIReportSubscription = supabaseClient
      .channel("appointment-ai-reports-update")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "appointment_ai_reports" },
        (payload) => {
          if (
            payload.new &&
            configStatus === "success" &&
            config?.show_read_studies_duration_days
          ) {
            fetchRef.current();
          }
        },
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "appointment_ai_reports" },
        (payload) => {
          if (
            payload.new &&
            configStatus === "success" &&
            config?.show_read_studies_duration_days
          ) {
            fetchRef.current();
          }
        },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "appointments" },
        (payload) => {
          if (
            payload.new &&
            payload.new.status !== payload.old.status &&
            configStatus === "success" &&
            config?.show_read_studies_duration_days
          ) {
            fetchRef.current();
          }
        },
      )
      .subscribe();

    return () => {
      supabaseClient.removeChannel(appointmentAIReportSubscription);
    };
  }, [isApprovedShown, configStatus, config?.show_read_studies_duration_days]);

  return {
    appointmentAIReports,
    isLoading,
    refetch: fetch,
  };
};

export default useTableAppointmentAIReports;
